// The engine's move generator compiled to WebAssembly (trainer/movegen-wasm,
// built by its build.sh into public/movegen.wasm). Same code as the importer's
// `damacli moves_paths`, so the board and the server cannot disagree (§2).
//
// The API also sends the legal moves of every position it shows, so the board
// works when the .wasm file has not been built; `legalMoves` returns null then
// and callers use the server's list.
import type { LegalMove } from "../api/client";

type Exports = {
  memory: WebAssembly.Memory;
  input_ptr: () => number;
  output_ptr: () => number;
  legal_moves: (len: number) => number;
};

let instance: Exports | null = null;
let loading: Promise<Exports | null> | null = null;
const enc = new TextEncoder();
const dec = new TextDecoder();

export function loadMovegen(): Promise<Exports | null> {
  if (!loading) {
    loading = (async () => {
      try {
        const res = await fetch("/movegen.wasm");
        if (!res.ok || !(res.headers.get("content-type") ?? "").includes("wasm")) return null;
        const { instance: inst } = await WebAssembly.instantiate(await res.arrayBuffer(), {});
        instance = inst.exports as unknown as Exports;
        return instance;
      } catch {
        return null;
      }
    })();
  }
  return loading;
}

/** Legal moves of a textfen, or null when the WASM module is not available. */
export function legalMoves(textfen: string): LegalMove[] | null {
  if (!instance) return null;
  const bytes = enc.encode(textfen);
  new Uint8Array(instance.memory.buffer, instance.input_ptr(), bytes.length).set(bytes);
  const len = instance.legal_moves(bytes.length);
  if (!len) return null;
  const json = dec.decode(new Uint8Array(instance.memory.buffer, instance.output_ptr(), len));
  const moves = JSON.parse(json) as Omit<LegalMove, "move">[];
  // canonical names: short unless start and end square are shared (see services/movegen.py)
  const shorts = moves.map((m) => `${m.from}${m.captured.length ? "x" : "-"}${m.to}`);
  return moves.map((m, i) => ({
    ...m,
    move: shorts.filter((s) => s === shorts[i]).length > 1 ? m.path.join("x") : shorts[i],
  }));
}
